/**
 * Form validation for trade booking
 */

import { TradeBookingForm, FormValidationErrors, TradeType, SettlementType } from '../types/tradeBooking';
import { isValidMaturity } from './maturityParser';

/**
 * Compare two dates ignoring time component
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if date1 is before date2 (ignoring time)
 */
const isDateBefore = (date1: Date, date2: Date): boolean => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return d1 < d2;
};

/**
 * Validate trade booking form
 * @param form - Form data to validate
 * @returns Validation errors object (empty if valid)
 */
export const validateTradeBookingForm = (form: TradeBookingForm): FormValidationErrors => {
  const errors: FormValidationErrors = {};

  // Trade Type (required)
  if (!form.tradeType) {
    errors.tradeType = 'tradeBooking.validation.selectTradeType';
  }

  // Direction (required)
  if (!form.direction) {
    errors.direction = 'tradeBooking.validation.selectDirection';
  }

  // Amount (required, must be positive)
  if (form.amount === null || form.amount === undefined) {
    errors.amount = 'tradeBooking.validation.required';
  } else if (isNaN(form.amount) || form.amount <= 0) {
    errors.amount = 'tradeBooking.validation.invalidNumber';
  }

  // Currency 1 (required)
  if (!form.currency1 || form.currency1.trim() === '') {
    errors.currency1 = 'tradeBooking.validation.selectCurrency';
  }

  // Price (required, must be positive — except Forward Starting where Price = 0)
  if (form.isForwardStarting) {
    // Forward Starting: price is 0 at booking, no validation needed
  } else if (form.price === null || form.price === undefined) {
    errors.price = 'tradeBooking.validation.required';
  } else if (isNaN(form.price) || form.price <= 0) {
    errors.price = 'tradeBooking.validation.invalidNumber';
  }

  // Currency 2 (required)
  if (!form.currency2 || form.currency2.trim() === '') {
    errors.currency2 = 'tradeBooking.validation.selectCurrency';
  }

  // Contra Amount (required, must be positive — except Forward Starting where Price = 0)
  if (form.isForwardStarting) {
    // Forward Starting: contra amount is 0 at booking (derived from price = 0)
  } else if (form.contraAmount === null || form.contraAmount === undefined) {
    errors.contraAmount = 'tradeBooking.validation.required';
  } else if (isNaN(form.contraAmount) || form.contraAmount <= 0) {
    errors.contraAmount = 'tradeBooking.validation.invalidNumber';
  }

  // Our Party (required)
  if (!form.ourParty || form.ourParty.trim() === '') {
    errors.ourParty = 'tradeBooking.validation.required';
  }

  // Counterparty (required)
  if (!form.counterparty || form.counterparty.trim() === '') {
    errors.counterparty = 'tradeBooking.validation.required';
  }

  // Trade Date (required)
  if (!form.tradeDate) {
    errors.tradeDate = 'tradeBooking.validation.required';
  } else if (isNaN(form.tradeDate.getTime())) {
    errors.tradeDate = 'tradeBooking.validation.invalidDate';
  }

  // Value Date (required)
  if (!form.valueDate) {
    errors.valueDate = 'tradeBooking.validation.required';
  } else if (isNaN(form.valueDate.getTime())) {
    errors.valueDate = 'tradeBooking.validation.invalidDate';
  } else if (form.tradeDate && isDateBefore(form.valueDate, form.tradeDate)) {
    errors.valueDate = 'tradeBooking.validation.valueDateBeforeTradeDate';
  } else if (form.isForwardStarting && form.tradeDate && !isDateBefore(form.tradeDate, form.valueDate)
    && (!form.forwardStartingFixingSchedule || form.forwardStartingFixingSchedule.length <= 1)) {
    // Multi-date averaged entry: ValueDate = TradeDate is valid (schedule captures observation dates)
    errors.valueDate = 'tradeBooking.validation.valueDateMustBeAfterTradeDate';
  }

  // Expiry Date (required)
  if (!form.expiryDate) {
    errors.expiryDate = 'tradeBooking.validation.required';
  } else if (isNaN(form.expiryDate.getTime())) {
    errors.expiryDate = 'tradeBooking.validation.invalidDate';
  } else if (form.valueDate && isDateBefore(form.expiryDate, form.valueDate)) {
    errors.expiryDate = 'tradeBooking.validation.expiryDateBeforeValueDate';
  }

  // Payment Date (required only for Compensación)
  if (form.settlementType !== SettlementType.ENTREGA_FISICA) {
    if (!form.paymentDate) {
      errors.paymentDate = 'tradeBooking.validation.required';
    } else if (isNaN(form.paymentDate.getTime())) {
      errors.paymentDate = 'tradeBooking.validation.invalidDate';
    } else if (form.expiryDate && isDateBefore(form.paymentDate, form.expiryDate)) {
      errors.paymentDate = 'tradeBooking.validation.paymentDateBeforeExpiryDate';
    }
  }

  // Valuta dates (required for Entrega Física)
  if (form.settlementType === SettlementType.ENTREGA_FISICA) {
    // Currency 1 Payment Date
    if (!form.currency1PaymentDate) {
      errors.currency1PaymentDate = 'tradeBooking.validation.required';
    } else if (isNaN(form.currency1PaymentDate.getTime())) {
      errors.currency1PaymentDate = 'tradeBooking.validation.invalidDate';
    } else if (form.expiryDate && isDateBefore(form.currency1PaymentDate, form.expiryDate)) {
      errors.currency1PaymentDate = 'tradeBooking.validation.valutaDateBeforeMaturity';
    }

    // Currency 2 Payment Date
    if (!form.currency2PaymentDate) {
      errors.currency2PaymentDate = 'tradeBooking.validation.required';
    } else if (isNaN(form.currency2PaymentDate.getTime())) {
      errors.currency2PaymentDate = 'tradeBooking.validation.invalidDate';
    } else if (form.expiryDate && isDateBefore(form.currency2PaymentDate, form.expiryDate)) {
      errors.currency2PaymentDate = 'tradeBooking.validation.valutaDateBeforeMaturity';
    }
  }

  // Forward-specific validations
  if (form.tradeType === TradeType.FORWARD) {
    // Maturity (required for forward)
    if (!form.maturity || form.maturity.trim() === '') {
      errors.maturity = 'tradeBooking.validation.required';
    } else if (!isValidMaturity(form.maturity)) {
      errors.maturity = 'tradeBooking.validation.invalidMaturity';
    }

    // Fixing Reference (required for forward)
    if (!form.fixingReference) {
      errors.fixingReference = 'tradeBooking.validation.required';
    }

    // Fixing Offset (required for forward)
    if (!form.fixingOffset) {
      errors.fixingOffset = 'tradeBooking.validation.required';
    }

    // Settlement Type (required for forward)
    if (!form.settlementType) {
      errors.settlementType = 'tradeBooking.validation.required';
    }

    // CLF (Forward de Inflación) must always be Compensación
    if (form.currency1 === 'CLF' && form.settlementType === SettlementType.ENTREGA_FISICA) {
      errors.settlementType = 'tradeBooking.validation.clfMustBeCompensacion';
    }

    // Settlement Currency (required for forward if Compensación)
    if (form.settlementType === SettlementType.COMPENSACION) {
      if (!form.settlementCurrency || form.settlementCurrency.trim() === '') {
        errors.settlementCurrency = 'tradeBooking.validation.selectCurrency';
      }
    }

    // Forward Asiático: require at least 2 fixing dates
    if (form.isAverageRateForward) {
      if (!form.averageRateFixingSchedule || form.averageRateFixingSchedule.length < 2) {
        errors.averageRateFixingSchedule = 'tradeBooking.validation.minFixingDates';
      }
    }

    // Forward Starting: require ForwardPoints and at least 1 fixing date
    if (form.isForwardStarting) {
      if (form.forwardPoints === null || form.forwardPoints === undefined) {
        errors.forwardPoints = 'tradeBooking.validation.forwardPointsRequired';
      }
      if (!form.forwardStartingFixingSchedule || form.forwardStartingFixingSchedule.filter(e => e.date).length < 1) {
        errors.forwardStartingFixingSchedule = 'tradeBooking.validation.forwardStartingMinDates';
      }
    }
  }

  return errors;
};

/**
 * Check if form has any validation errors
 * @param errors - Validation errors object
 * @returns True if form is valid (no errors)
 */
export const isFormValid = (errors: FormValidationErrors): boolean => {
  return Object.keys(errors).length === 0;
};

/**
 * Get first error message from errors object
 * @param errors - Validation errors object
 * @returns First error message or null
 */
export const getFirstError = (errors: FormValidationErrors): string | null => {
  const keys = Object.keys(errors);
  if (keys.length === 0) return null;
  return errors[keys[0] as keyof FormValidationErrors] || null;
};

/**
 * Get the number of validation errors
 * @param errors - Validation errors object
 * @returns Number of errors
 */
export const getErrorCount = (errors: FormValidationErrors): number => {
  return Object.keys(errors).length;
};

/**
 * Get the first error field key
 * @param errors - Validation errors object
 * @returns First error field key or null
 */
export const getFirstErrorField = (errors: FormValidationErrors): string | null => {
  const keys = Object.keys(errors);
  if (keys.length === 0) return null;
  return keys[0];
};
